import React, { useRef, useEffect } from 'react';
import QRious from 'qrious';
import { SOCIAL_NETWORKS, getSocialIcon } from '../../utils/socialNetworks';
import { replaceShortcodesWithExamples, replaceShortcodesWithData } from '../../utils/adAttributes';
import { getIconApiUrl } from '../../utils/iconUtils';
import { useOrganization } from '../../contexts/OrganizationContext';

// Convertir les données d'un membre de l'annuaire en format compatible avec les shortcodes
// Les données peuvent venir soit de la BDD locale, soit de l'AD (via getLiveUserData)
const mapMemberToUserData = (member) => {
  if (!member) return null;
  return {
    // Identité
    givenName: member.first_name || '',
    sn: member.last_name || '',
    displayName: member.display_name || `${member.first_name || ''} ${member.last_name || ''}`.trim(),
    initials: `${(member.first_name || '')[0] || ''}${(member.last_name || '')[0] || ''}`.toUpperCase(),
    sAMAccountName: member.email?.split('@')[0] || '',
    // Contact
    mail: member.email || '',
    telephoneNumber: member.phone || '',
    mobile: member.mobile || '',
    facsimileTelephoneNumber: member.fax || '',
    ipPhone: member.ip_phone || '',
    // Organisation
    title: member.title || '',
    department: member.department || '',
    company: member.company || '',
    manager: member.manager || '',
    physicalDeliveryOfficeName: member.office || '',
    // Adresse (noms de champs de l'API live: address, city, state, postal_code, country)
    streetAddress: member.address || member.street || '',
    l: member.city || '',
    st: member.state || '',
    postalCode: member.postal_code || '',
    co: member.country || '',
    // Web & Social
    wWWHomePage: member.website || '',
    extensionAttribute1: member.linkedin || '',
    extensionAttribute2: member.twitter || '',
    extensionAttribute3: member.facebook || '',
    // Autres
    description: member.description || '',
    employeeID: member.employee_id || '',
    employeeNumber: member.employee_number || '',
    // Personnalisation
    greeting: member.greeting || '',
    profile_photo: member.profile_photo || '',
    org_logo: member.org_logo || ''
  };
};

function ElementRenderer({ element, previewMode = false, previewMember = null, demoAssets = null }) {
  const qrCodeRef = useRef(null);
  const { logoUrl } = useOrganization();
  const style = element.style || {};

  // Fonction pour remplacer les shortcodes en mode preview
  const processValue = (value) => {
    if (!value || typeof value !== 'string') return value;
    if (!previewMode) return value;

    // Si un membre est sélectionné, utiliser ses données réelles
    if (previewMember) {
      const userData = mapMemberToUserData(previewMember);
      return replaceShortcodesWithData(value, userData);
    }
    // Sinon utiliser les données de démo (Jean Dupont)
    return replaceShortcodesWithExamples(value);
  };

  // Fonction utilitaire pour extraire les marges et créer l'objet de style
  const getMarginStyle = () => {
    return {
      marginTop: style.marginTop || '0px',
      marginRight: style.marginRight || '0px',
      marginBottom: style.marginBottom || '0px',
      marginLeft: style.marginLeft || '0px'
    };
  };

  useEffect(() => {
    if (element.type === 'qrcode' && qrCodeRef.current) {
      try {
        new QRious({
          element: qrCodeRef.current,
          value: element.qrData || element.value || 'https://example.com',
          size: parseInt(style.size || '128'),
          foreground: style.fgColor || '#000000',
          background: style.bgColor || '#ffffff',
          level: 'H'
        });
      } catch (error) {
        console.error('QR Code generation error:', error);
      }
    }
  }, [element, style]);

  switch (element.type) {
    case 'text':
      const displayValue = processValue(element.value);
      const textContent = (
        <span style={{
          color: style.color,
          fontSize: style.fontSize,
          fontFamily: style.fontFamily,
          fontWeight: style.fontWeight,
          fontStyle: style.fontStyle,
          textDecoration: style.textDecoration
        }}>
          {displayValue}
        </span>
      );

      if (element.link) {
        return (
          <div style={{ textAlign: style.textAlign || 'left', ...getMarginStyle() }}>
            <a href={processValue(element.link)} style={{ color: 'inherit', textDecoration: 'none' }}>
              {textContent}
            </a>
          </div>
        );
      }

      return (
        <div style={{
          color: style.color,
          fontSize: style.fontSize,
          fontFamily: style.fontFamily,
          fontWeight: style.fontWeight,
          fontStyle: style.fontStyle,
          textDecoration: style.textDecoration,
          textAlign: style.textAlign || 'left',
          ...getMarginStyle()
        }}>
          {displayValue}
        </div>
      );
    
    case 'richtext':
      return <div style={{ ...getMarginStyle() }} dangerouslySetInnerHTML={{ __html: processValue(element.value) }} />;
    
    case 'image':
      const imgStyle = {
        maxWidth: style.width || '200px',
        width: style.width || 'auto'
      };
      if (style.height && style.height !== '0' && style.height !== 'auto') {
        imgStyle.height = style.height;
        imgStyle.objectFit = 'cover';
      }
      // Appliquer le format d'image (carré, arrondi, cercle)
      if (style.imageShape === 'circle') {
        imgStyle.borderRadius = '50%';
      } else if (style.imageShape === 'rounded') {
        imgStyle.borderRadius = style.borderRadius || '8px';
      }

      const imageElement = <img src={element.value} alt={element.alt || ''} style={imgStyle} />;

      if (element.link) {
        return (
          <div className="element-image" style={{ textAlign: style.textAlign || 'left', ...getMarginStyle() }}>
            <a href={element.link}>
              {imageElement}
            </a>
          </div>
        );
      }

      return <div className="element-image" style={{ textAlign: style.textAlign || 'left', ...getMarginStyle() }}>{imageElement}</div>;

    case 'profilephoto':
      // Photo de profil - utilise le shortcode {profile_photo} ou une image par défaut
      const profilePhotoAlign = style.textAlign || 'left';
      const profilePhotoStyle = {
        width: style.width || '100px',
        height: style.height || '100px',
        objectFit: 'cover',
        display: 'block',
        // Alignement via margin pour les éléments block
        marginLeft: profilePhotoAlign === 'center' ? 'auto' : (profilePhotoAlign === 'right' ? 'auto' : '0'),
        marginRight: profilePhotoAlign === 'center' ? 'auto' : '0'
      };
      // Appliquer le format d'image (carré, arrondi, cercle)
      if (style.imageShape === 'circle') {
        profilePhotoStyle.borderRadius = '50%';
      } else if (style.imageShape === 'rounded') {
        profilePhotoStyle.borderRadius = style.borderRadius || '8px';
      }

      // En preview, utiliser l'image de démo si disponible, sinon placeholder
      const defaultPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ccircle cx="50" cy="35" r="20" fill="%239ca3af"/%3E%3Cellipse cx="50" cy="85" rx="35" ry="30" fill="%239ca3af"/%3E%3C/svg%3E';
      const profilePhotoSrc = element.value || demoAssets?.profilePhoto || defaultPlaceholder;

      const profilePhotoElement = <img src={profilePhotoSrc} alt="Photo de profil" style={profilePhotoStyle} />;

      if (element.link) {
        return (
          <div className="element-profilephoto" style={{ ...getMarginStyle() }}>
            <a href={element.link}>
              {profilePhotoElement}
            </a>
          </div>
        );
      }

      return <div className="element-profilephoto" style={{ ...getMarginStyle() }}>{profilePhotoElement}</div>;

    case 'orglogo':
      // Logo de l'organisation - utilise le logo configuré dans les paramètres organisation
      const orgLogoAlign = style.textAlign || 'left';
      const orgLogoStyle = {
        width: style.width || '150px',
        height: style.height || 'auto',
        objectFit: 'contain',
        display: 'block',
        // Alignement via margin pour les éléments block
        marginLeft: orgLogoAlign === 'center' ? 'auto' : (orgLogoAlign === 'right' ? 'auto' : '0'),
        marginRight: orgLogoAlign === 'center' ? 'auto' : '0'
      };

      // Construire l'URL complète du logo (priorité: demoAssets > logoUrl organisation)
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const orgLogoSrc = demoAssets?.logo || (logoUrl ? `${backendUrl}${logoUrl}` : null);

      // Placeholder si pas de logo
      const orgLogoPlaceholder = (
        <div
          style={{
            width: style.width || '150px',
            height: style.height || '80px',
            backgroundColor: '#f3f4f6',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: '12px',
            textAlign: 'center',
            padding: '8px',
            // Alignement via margin pour les éléments block
            marginLeft: orgLogoAlign === 'center' ? 'auto' : (orgLogoAlign === 'right' ? 'auto' : '0'),
            marginRight: orgLogoAlign === 'center' ? 'auto' : '0'
          }}
        >
          <span>Logo organisation<br/>(non configuré)</span>
        </div>
      );

      const orgLogoElement = orgLogoSrc ? (
        <img src={orgLogoSrc} alt="Logo de l'organisation" style={orgLogoStyle} />
      ) : orgLogoPlaceholder;

      return (
        <div className="element-orglogo" style={{ ...getMarginStyle() }}>
          {orgLogoElement}
        </div>
      );

    case 'banner':
      return (
        <div className="element-banner" style={{ ...getMarginStyle() }}>
          <a href={element.link || '#'}>
            <img src={element.value} alt={element.alt || 'Banner'} style={{ height: style.height || '150px', objectFit: 'cover' }} />
          </a>
        </div>
      );
    
    case 'button':
      return (
        <a
          href={processValue(element.link) || '#'}
          className="element-button"
          style={{
            backgroundColor: style.backgroundColor,
            color: style.color,
            padding: style.padding || '12px 24px',
            borderRadius: style.borderRadius || '6px',
            ...getMarginStyle()
          }}
        >
          {processValue(element.value)}
        </a>
      );
    
    case 'icontext':
      const iconType = element.iconType || 'emoji';
      const iconColor = style.iconColor || style.color || '#000000';
      let iconContent;

      if (iconType === 'library' && element.libraryIcon) {
        // Icône de la bibliothèque - utiliser l'API pour générer l'icône colorée
        const iconSize = parseInt(style.iconSize || '20');
        iconContent = (
          <img
            src={getIconApiUrl(element.libraryIcon, iconColor, iconSize)}
            alt={element.libraryIcon.name}
            style={{
              width: style.iconSize || '20px',
              height: style.iconSize || '20px',
              display: 'block'
            }}
          />
        );
      } else if (iconType === 'image') {
        // Image personnalisée
        iconContent = (
          <img
            src={element.iconUrl || ''}
            alt="icon"
            style={{
              width: style.iconSize || '20px',
              height: style.iconSize || '20px',
              display: 'block'
            }}
          />
        );
      } else {
        // Emoji
        iconContent = (
          <span className="icon" style={{ fontSize: style.iconSize || '20px', color: iconColor }}>{element.icon || '📧'}</span>
        );
      }

      const iconTextValue = processValue(element.text || element.value);
      return (
        <div className="element-icontext" style={{ color: style.color, fontSize: style.fontSize, ...getMarginStyle() }}>
          {iconContent}
          {element.link ? (
            <a href={processValue(element.link)} style={{ color: 'inherit', textDecoration: 'none' }}>{iconTextValue}</a>
          ) : (
            <span>{iconTextValue}</span>
          )}
        </div>
      );
    
    case 'divider':
      return (
        <div
          className="element-divider"
          style={{
            borderTop: `${style.height || '1px'} ${style.borderStyle || 'solid'} ${style.color || '#e0e0e0'}`,
            ...getMarginStyle()
          }}
        />
      );
    
    case 'spacer':
      return <div style={{ height: style.height || '20px', ...getMarginStyle() }} />;
    
    case 'social':
      const network = SOCIAL_NETWORKS[element.network] || SOCIAL_NETWORKS.linkedin;
      const socialIconStyle = style.iconStyle || 'flat';
      return (
        <div className="element-social" style={{ ...getMarginStyle() }}>
          <a href={element.link || '#'} target="_blank" rel="noopener noreferrer">
            <img src={getSocialIcon(element.network || 'linkedin', socialIconStyle)} alt={network.name} style={{ width: style.size || '32px', height: style.size || '32px' }} />
          </a>
        </div>
      );
    
    case 'socialgroup':
      const networks = element.networks || [];
      const iconStyle = style.iconStyle || 'flat';
      return (
        <div className="element-socialgroup" style={{ gap: style.spacing || '8px', justifyContent: style.align || 'flex-start', ...getMarginStyle() }}>
          {networks.map((netKey, idx) => {
            const net = SOCIAL_NETWORKS[netKey];
            if (!net) return null;
            const link = element.links?.[netKey] || '#';
            return (
              <a key={idx} href={link} target="_blank" rel="noopener noreferrer">
                <img src={getSocialIcon(netKey, iconStyle)} alt={net.name} style={{ width: style.size || '32px', height: style.size || '32px' }} />
              </a>
            );
          })}
        </div>
      );
    
    case 'qrcode':
      return (
        <div className="element-qrcode" style={{ textAlign: style.align || 'center', padding: style.margin || '0', ...getMarginStyle() }}>
          <canvas ref={qrCodeRef} />
        </div>
      );
    
    default:
      return <div>{element.value}</div>;
  }
}

export default ElementRenderer;
